'use client'

import { SiteNav } from '@/components/ui/site-nav'
import { useSearchParams, useRouter } from 'next/navigation'
import Link from 'next/link'
import { Suspense, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useGameState } from '@/hooks/useGameState'
import { useWallet } from '@/hooks/useWallet'
import { useSoundscape, GAME_OVER_TRACKS, playSting } from '@/hooks/useSoundscape'
import { useHeartbeat } from '@/hooks/useHeartbeat'
import { useSound } from '@/providers/sound-provider'
import { createContractClient } from '@/lib/contract'
import { formatToken } from '@/lib/format'
import { quarantineCode } from '@/lib/roomLabel'
import { checkChatMessage } from '@/lib/chatFilter'
import { GameTabNav, type GameTab } from '@/components/game/GameTabNav'
import { AmbientLayer } from '@/components/game/AmbientLayer'
import { PhaseTransition } from '@/components/game/PhaseTransition'
import { PhaseCoach } from '@/components/game/PhaseCoach'
import { ModifierBanner } from '@/components/game/ModifierBanner'
import { useNarrative } from '@/hooks/useNarrative'
import type { NarrativeSet } from '@/lib/narrative'
import { ArenaDoors } from '@/components/game/ArenaDoors'
import { ArenaHub } from '@/components/game/ArenaHub'
import { MomentOverlay, type Moment } from '@/components/game/MomentOverlay'
import { PlayersGrid } from '@/components/game/PlayersGrid'
import { sweepDurationMs } from '@/lib/containment-sweep'
import { usePresence } from '@/hooks/usePresence'
import { useBarricade } from '@/hooks/useBarricade'
import { usePlayerProgress } from '@/hooks/usePlayerProgress'
import { BarricadeBoard } from '@/components/game/BarricadeBoard'
import { PasswordInput } from '@/components/ui/password-input'
import { OutbreakScene } from '@/components/game/OutbreakScene'
import { GameOverOverlay, type GameOutcome } from '@/components/game/GameOverOverlay'
import { BotControls } from '@/components/lobby/bot-controls'
import type { RoundPhase } from '@/types/game'
import { toast } from 'sonner'

export const dynamic = 'force-dynamic'

// ── Phase display helpers ─────────────────────────────────────────────────────

// Phase names and colours live in lib/narrative.ts so a theme can rename them
// ("VOTING" vs "THE JUDGEMENT"). Pre-game statuses below are theme-neutral.

// A room only has a round (and therefore a phase) once it is `active`. Before
// that `currentRound` is undefined and `phase` falls back to `ended` — which
// rendered "ENDED" and "Game ended." on the phase card of a game that had not
// started yet, reading to players as if they had walked in on a dead room.
// These three resolve the pre-game statuses to their own label/colour instead.
function getPhaseLabel(n: NarrativeSet, phase: RoundPhase, roomStatus?: string): string {
  if (roomStatus === 'waiting')  return 'LOBBY'
  if (roomStatus === 'starting') return 'SETUP'
  return n.phaseLabels[phase]
}

function getPhaseColor(n: NarrativeSet, phase: RoundPhase, roomStatus?: string): string {
  if (roomStatus === 'waiting')  return '#7d9a72'
  if (roomStatus === 'starting') return '#f5c518'
  return n.phaseColors[phase]
}

/** Header badge text. Reads as a sentence, so it can't render "LOBBY phase". */
function getPhaseBadgeText(n: NarrativeSet, phase: RoundPhase, roomStatus?: string): string {
  if (roomStatus === 'waiting')  return 'Waiting for players'
  if (roomStatus === 'starting') return 'Setup — commit window'
  if (roomStatus === 'ended' || phase === 'ended') return 'Game ended'
  return `${n.phaseLabels[phase]} phase`
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const CUSD_ADDRESSES: Record<number, `0x${string}`> = {
  11142220: '0xae10a9e08d979e7d154d3b0212fb7cbf70fa6bb1', // Celo Sepolia (Mock USDm)
  42220: '0x765DE816845861e75A25fCA122bb6898B8B1282a',   // Mainnet (USDm)
}
const ROLE_COMMIT_TIMEOUT_MS = Number(process.env.NEXT_PUBLIC_ROLE_COMMIT_TIMEOUT_MS ?? 180_000)

function getHeaderTitle(isLoading: boolean, round: number, roomStatus?: string): string {
  if (isLoading) return 'LOADING…'
  if (round > 0) return `ROUND ${round}`
  if (roomStatus === 'waiting') return 'WAITING'
  if (roomStatus === 'ended') return 'ENDED'
  return 'STARTING'
}

function getPhaseCardBackground(phase: RoundPhase, roomStatus?: string): string {
  if (roomStatus === 'starting') return 'rgba(245,197,24,0.1)'
  if (roomStatus === 'waiting') return 'rgba(143,168,130,0.1)'
  if (phase === 'infection') return 'rgba(230,51,41,0.1)'
  if (phase === 'discussion') return 'rgba(107,142,35,0.1)'
  if (phase === 'voting') return 'rgba(245,197,24,0.1)'
  return 'rgba(143,168,130,0.1)'
}

function getPhaseDescription(
  phase: RoundPhase,
  hasVoted: boolean,
  result: { outcome: string } | null,
  roomStatus?: string,
  isInfected?: boolean,
): string {
  // Kept to roughly one line each. On a phone these wrapped to three or four,
  // and the phase card is the thing sitting between a player and the board —
  // the sentence they need is "what do I do now", not the lore.
  if (phase === 'infection') return isInfected
    ? 'You are a carrier. A new target is being infected.'
    : 'A new carrier is being assigned.'
  if (phase === 'discussion') return 'Activate your Shield before voting opens.'
  if (phase === 'voting') return hasVoted ? 'Vote cast. Waiting on the others…' : 'Vote out the suspected carrier.'
  if (phase === 'reveal') return 'Resolving the elimination on-chain…'
  // Status is checked ahead of `result` here: a room can carry an aborted
  // result while sitting in `waiting`, and "Game over" is the wrong first
  // thing to say to someone who just walked into a room that hasn't started.
  if (roomStatus === 'waiting') return 'Waiting for players to join.'
  if (roomStatus === 'starting') return 'Set your Shield Password — the game starts once everyone has.'
  if (result) return `Game over: ${result.outcome.replaceAll('_', ' ')}`
  return 'Game ended.'
}

function getResultLabel(outcome: string): string {
  if (outcome === 'aborted') return 'No Contest — Stakes Refunded'
  if (outcome === 'clean_win') return 'Clean Win'
  if (outcome === 'infected_win') return 'Infected Win'
  return 'Draw'
}

function getVotePanelLabel(phase: RoundPhase, hasVoted: boolean): string {
  if (phase !== 'voting') return 'Voting not open'
  return hasVoted ? 'Vote submitted' : 'Select suspected carrier'
}

function getChatBlockedReason(
  socketOnline: boolean,
  localPlayer: { isEliminated: boolean } | null,
  roomStatus: string | undefined,
  phase: RoundPhase,
): string | null {
  if (!socketOnline) return 'Chat unavailable while backend is offline.'
  if (localPlayer === null) return 'Only joined room players can chat.'
  if (roomStatus === 'ended') return 'Chat is closed because this game has ended.'
  if (localPlayer.isEliminated) return 'Eliminated players cannot use chat.'
  if (roomStatus === 'active' && phase === 'voting') return 'Chat is disabled during voting.'
  return null
}

function ensureHexPrefixed(value: string): `0x${string}` {
  if (value.startsWith('0x')) return value as `0x${string}`
  return `0x${value}`
}

// ── Contract error → user message ─────────────────────────────────────────────

const CONTRACT_ERROR_MAP: Record<string, string> = {
  NotAlive:              'You are no longer alive in this game and cannot take this action.',
  WrongPhase:            'This action is not available in the current phase.',
  AlreadyVoted:          'You have already voted this round.',
  AlreadyProvedThisRound:'Your Shield is already active this round.',
  NullifierUsed:         'This proof nullifier has already been used. Try a different secret.',
  InvalidProof:          'ZK proof verification failed. Check your secret phrase and try again.',
  NotParticipant:        'You are not a participant in this room.',
  AlreadyCommitted:      'You have already committed your role.',
  DuplicateRoleCommitment:'That Shield Password is already taken in this room. Pick a different one.',
  NotActive:             'The game is not currently active.',
  NotEnoughPlayers:      'Not enough players to perform this action.',
  InvalidInfectionTarget:'Invalid infection target.',
}

function parseContractError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err)
  if (/cannot satisfy constraint|cannot satisfy constraints|unsatisfied constraint/i.test(msg)) {
    return 'Proof generation failed. Your secret phrase is incorrect for this room/round. Please use the exact phrase you committed with.'
  }
  for (const [name, friendly] of Object.entries(CONTRACT_ERROR_MAP)) {
    if (msg.includes(name)) return friendly
  }
  if (isTransientRpcError(err)) return TRANSIENT_RPC_MESSAGE
  return msg.split('\n')[0]
}

const TRANSIENT_RPC_MESSAGE = 'Network hiccup — nothing was charged. Tap the button again.'

/**
 * Did this fail because the network dropped it, rather than because the
 * transaction was wrong?
 *
 * Celo reads go through load-balanced nodes that intermittently time out or
 * return a response viem cannot classify, which surfaces as the useless
 * "An unknown RPC error occurred". Reported from a phone: activating a Shield
 * failed with exactly that, and the identical retry a moment later succeeded.
 *
 * These are worth distinguishing because the advice is the opposite of a real
 * failure. A revert means stop and fix something; this means press it again.
 * Matching is deliberately narrow — a contract revert must never be described
 * as a hiccup, so anything with a decoded reason is handled above this.
 */
function isTransientRpcError(err: unknown): boolean {
  const seen = new Set<unknown>()
  let cur: unknown = err
  while (cur && typeof cur === 'object' && !seen.has(cur)) {
    seen.add(cur)
    const o = cur as Record<string, unknown>
    const text = [o.shortMessage, o.details, o.message].filter(v => typeof v === 'string').join(' ')
    if (/unknown rpc error|request timed out|timeout|failed to fetch|network ?error|internal error|service unavailable|502|503|504/i.test(text)) {
      return true
    }
    cur = o.cause
  }
  return false
}

/** Run `fn`, retrying once if the network dropped it rather than rejected it. */
async function retryOnTransient<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch (err) {
    if (!isTransientRpcError(err)) throw err
    // Short pause so the retry has a chance of landing on a healthier node
    // rather than hammering the one that just failed.
    await new Promise(r => setTimeout(r, 1_200))
    return fn()
  }
}

// ── Inner component (uses hooks that need Suspense) ───────────────────────────

function GamePageInner() { // NOSONAR
  const router = useRouter()
  const params = useSearchParams()
  const roomId = params.get('room')

  const { isConnected, address, chainId } = useWallet()
  const { room, localPlayer, currentRound, result, isConnected: socketOn, isLoading, error, feed, socket, refresh } = useGameState(roomId, address)

  // ── Phase timer ──────────────────────────────────────────────────────────
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(id)
  }, [])

  const phaseEndsAt = currentRound?.phaseEndsAt ?? 0
  const msLeft = phaseEndsAt > now ? phaseEndsAt - now : 0
  const startCommitEndsAt = room?.status === 'starting' && room?.startedAt
    ? room.startedAt + ROLE_COMMIT_TIMEOUT_MS
    : 0
  const headerCountdownMs = startCommitEndsAt > now ? startCommitEndsAt - now : msLeft

  // ── Vote state ───────────────────────────────────────────────────────────
  const [selectedVote, setSelectedVote]           = useState<string | null>(null)
  const [voting, setVoting]                       = useState(false)
  const [voteError, setVoteError]                 = useState<string | null>(null)
  const [optimisticVotedFor, setOptimisticVotedFor] = useState<string | null>(null)

  // ── Proof submission state ───────────────────────────────────────────────
  const [proving, setProving]       = useState(false)
  const [proofError, setProofError] = useState<string | null>(null)
  const [optimisticProofDone, setOptimisticProofDone] = useState(false)

  // ── Game-over reveal overlay ─────────────────────────────────────────────
  const [gameOverDismissed, setGameOverDismissed] = useState(false)

  // ── Personal moment overlay (private beats: infection reveal, shield) ────
  const [moment, setMoment] = useState<{ key: string; data: Moment } | null>(null)

  // ── Round-opening containment sweep (shared, public, status-blind) ───────
  // Declared here rather than beside the effect that fills it: the players
  // panel is built further down this render, so the binding has to exist before
  // that point.
  const [sweep, setSweep] = useState<{ seed: string; anchor: number } | null>(null)

  // ── Role commitment state (during Starting phase) ────────────────────────
  const [committing, setCommitting]               = useState(false)
  const [commitError, setCommitError]             = useState<string | null>(null)
  const [secretPhrase, setSecretPhrase]           = useState('')
  const [optimisticCommitDone, setOptimisticCommitDone] = useState(false)

  // ── Start game state (host only, Waiting phase) ──────────────────────────
  const [starting, setStarting]         = useState(false)
  const [startError, setStartError]     = useState<string | null>(null)

  // ── Room name editor (host only, when name is null) ─────────────────────
  const [roomNameEditing, setRoomNameEditing] = useState(false)
  const [pendingRoomName, setPendingRoomName] = useState('')

  // ── Chat state ───────────────────────────────────────────────────────────
  type ChatMsg = { sender: string; displayName: string; message: string; timestamp: number }
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([])
  const [chatInput, setChatInput]       = useState('')
  const chatScrollRef = useRef<HTMLDivElement>(null)
  // Whether the reader is sitting at the bottom of the chat log. Recorded on
  // scroll (i.e. before new content arrives) so the auto-scroll effect can tell
  // "was at the bottom" from "scrolled up reading history".
  const chatPinnedRef = useRef(true)
  const phaseAdvanceNudgeKeyRef = useRef<string>('')

  // Live "who is composing" signal. Purely social information — see
  // hooks/usePresence.ts for the rate-limiting that keeps it off the egress cap.
  const { typingAddrs, noteActivity, noteSent } = usePresence(socket, roomId, address)

  // Discussion-phase barricade. The seat index is the player's position in the
  // chain-ordered roster, which is what the server derives station assignments
  // from — so it must come from room.players, not from any filtered view.
  const mySeatIndex = room?.players?.findIndex(
    p => p.walletAddress.toLowerCase() === (address ?? '').toLowerCase(),
  ) ?? -1
  const barricade = useBarricade(socket, roomId, address, mySeatIndex)


  // The return hook. Fetched only once the game is actually over — see the
  // note in usePlayerProgress about why the exit moment is the only one that
  // matters here. An aborted room is a refund, not a played game, so it has
  // nothing to report.
  // `currentRound` rather than the derived `round`, which is declared further
  // down this render.
  const playerProgress = usePlayerProgress(
    address,
    room?.status === 'ended' && (currentRound?.number ?? 0) > 0,
  )

  // ── Mobile tab navigation ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<GameTab>('play')
  // Phone-only: the pot/infected/shield strip starts collapsed. Closed by
  // default because the three numbers on the strip are the whole answer most of
  // the time; the expansion explains what they mean, which you need once.
  const [statsOpen, setStatsOpen] = useState(false)
  const [unreadChat, setUnreadChat] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  // Refs to expose current values inside socket-handler closures without re-registering
  const activeTabRef = useRef<GameTab>('play')
  const isMobileRef  = useRef(false)
  useEffect(() => { activeTabRef.current = activeTab }, [activeTab])
  useEffect(() => { isMobileRef.current  = isMobile  }, [isMobile])
  useEffect(() => {
    // 767.98 so this is the exact complement of Tailwind's `md:` (min-width
    // 768px) and of the .game-tab-bar rule in globals.css. A plain 768 here
    // overlapped both by a pixel, and that pixel is iPad portrait.
    const mq = window.matchMedia('(max-width: 767.98px)')
    setIsMobile(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Clear unread when switching to chat tab
  const handleTabChange = useCallback((tab: GameTab) => {
    setActiveTab(tab)
    if (tab === 'chat') setUnreadChat(0)
  }, [])

  // Pull a player back to Play when the game starts wanting something from them.
  //
  // This used to run on every phase change and pick between the Game and Vote
  // tabs. Now that both are one tab there is nothing to choose, and no reason to
  // fire mid-round — the action panel is already at the top of Play whatever the
  // phase. So it only fires on the transitions where a player reading Chat or
  // Feed would genuinely miss their turn.
  useEffect(() => {
    if (!isMobile) return
    if (room?.status === 'starting' || room?.status === 'active') setActiveTab('play')
  }, [room?.status, isMobile])

  const schedulePostTxRefresh = useCallback(() => {
    socket?.emit('request_room_refresh', { roomId })
    // Retry refreshes at 1.5 s, 3.5 s, and 6 s to handle RPC propagation lag.
    setTimeout(() => { refresh(); socket?.emit('request_room_refresh', { roomId }) }, 1_500)
    setTimeout(() => { refresh(); socket?.emit('request_room_refresh', { roomId }) }, 3_500)
    setTimeout(() => { refresh() }, 6_000)
  }, [refresh, roomId, socket])

  // ── Derived ──────────────────────────────────────────────────────────────
  const phase       = currentRound?.phase ?? 'ended'
  const round       = currentRound?.number ?? 0
  /** Shields can only be activated during Discussion — see PlagueGame.submitInnocenceProof. */
  const shieldWindowOpen = phase === 'discussion'

  // Round-opening containment sweep — see the LATCH note at the useEffect below.
  const sweepArmed = phase === 'infection' && room?.status === 'active' && round > 0
    ? `${roomId}:${round}`
    : null
  const activePlayers = room?.players?.filter(p => !p.isEliminated) ?? []
  const totalPlayers  = room?.players?.length ?? 0
  const infectedCount = room?.players?.filter(p => p.status === 'infected' && !p.isEliminated).length ?? 0
  // Infected majority reached, but the room hasn't ended yet — the contract
  // only settles this at the next infection assignment. Drives the "outbreak
  // decided" banner so the gap doesn't look like a broken rule.
  const outbreakDecided =
    room?.status === 'active' && infectedCount > activePlayers.length - infectedCount
  // What the quarantine cam draws. The barricade and the cam used to be two
  // panels describing the same room and disagreeing about its geometry — the
  // cam showed figures wandering an open chamber while the panel insisted you
  // were posted at a window. The cam IS the barricade now, so there is one
  // fiction, and the rules are legible by watching rather than by reading.
  const lastPush = barricade.state?.outcomes[barricade.state.outcomes.length - 1] ?? null
  // The compound is drawn for the WHOLE game, not only while the barricade is
  // running. A room that becomes a fortified courtyard for three minutes and an
  // empty chamber the rest of the time reads as two different games; the place
  // should persist and only what happens in it should change by phase.
  const barricadeRunning = !!barricade.state && phase === 'discussion'
  const barricadeState = barricade.state
  const barricadeMyStation = barricade.myStation
  const roomStatus = room?.status
  // 🚨 MEMOIZED, AND IT MATTERS.
  // The scene treats a new object here as "the board changed" and re-aims every
  // figure. A fresh literal on every render meant that happened dozens of times
  // a second, so the room twitched between destinations and never actually
  // walked anywhere.
  // 🚨 EVERY STATUS DRAWS THE COMPOUND. There is no room without walls.
  //
  // This was gated first on 'active', then on 'active' | 'starting', and each
  // time the remaining status was the one a player actually sits and stares at:
  // figures milling in an empty void while the lobby fills, then walls
  // appearing out of nowhere when the game began. The demo has never had a gate
  // and shows the compound from its first frame, which is why the two read as
  // different games.
  //
  // `active` below still gates the MECHANIC to Discussion, so the walls stand
  // empty until there is something to defend — what changes here is only
  // whether there is a compound to draw at all, and the answer is always yes.
  const barricadeView = useMemo(() => roomStatus
    ? {
        active: barricadeRunning,
        // Empty outside Discussion, so figures mill about inside rather than
        // posting at walls nobody is defending.
        occupancy: barricadeRunning ? barricadeState?.occupancy ?? [] : [],
        threatened: barricadeRunning ? barricadeState?.next?.station ?? null : null,
        // Keyed on how many pushes have resolved, so two consecutive breaches
        // both fire instead of the second being swallowed as "no change".
        myStation: barricadeRunning ? barricadeMyStation : null,
        resultKey: barricadeState?.outcomes.length ?? 0,
        held: lastPush?.held ?? null,
        // 🚨 DAMAGE OUTLIVES THE PHASE. A wall that took a breach stays
        // splintered until the round is over — this was gated on the barricade
        // still RUNNING, so the moment Discussion ended every broken wall
        // silently boarded itself back up while the player watched. A wall
        // repairing itself in front of you reads as a bug, and it was one.
        // (It re-boards at the next round, under the phase transition, which is
        // the survivors patching it between nights.)
        brokenWalls: barricadeState?.outcomes.filter(o => !o.held).map(o => o.station) ?? [],
        infectedCount,
        // The endgame the HUD already reports as a line of text. Drawing it is
        // the point: the most dramatic moment in the game was a sentence.
        collapsed: outbreakDecided,
      }
    : null,
    [roomStatus, barricadeRunning, barricadeState, barricadeMyStation, lastPush, infectedCount, outbreakDecided])

  const potCUSD       = room ? formatToken(Number(room.stakeAmount) * totalPlayers) : '—'
  const hasVoted      = Boolean(optimisticVotedFor || localPlayer?.hasVotedThisRound)
  const myVotedTarget = optimisticVotedFor ?? localPlayer?.voteTarget
  const hasProofThisRound = optimisticProofDone || Boolean(localPlayer?.hasProofThisRound)
  const commitDone = optimisticCommitDone || Boolean(localPlayer?.roleCommitted)
  // Shields raised by OTHER players this round (public info; feeds the quarantine cam)
  const othersShieldCount = room?.players?.filter(p =>
    p.hasProofThisRound && !p.isEliminated && p.walletAddress.toLowerCase() !== (address ?? '').toLowerCase()
  ).length ?? 0
  const canVote       = phase === 'voting' && isConnected && !!localPlayer && !localPlayer.isEliminated && !hasVoted && !voting
  const canProve      = phase === 'discussion' && isConnected && !!localPlayer && !localPlayer.isEliminated && localPlayer.status !== 'infected' && !hasProofThisRound
  const canCommit     = room?.status === 'starting' && isConnected && !!localPlayer && !committing && !commitDone
  const chatBlockedReason = getChatBlockedReason(socketOn, localPlayer, room?.status, phase)
  const canChat = chatBlockedReason === null

  // Nudge players to set their Shield Password: once when the commit window
  // opens, and again (urgent) when under a minute remains. Ref-gated so each
  // fires at most once per starting window.
  const commitNudgedRef = useRef(false)
  const commitUrgentNudgedRef = useRef(false)
  useEffect(() => {
    if (room?.status !== 'starting') {
      commitNudgedRef.current = false
      commitUrgentNudgedRef.current = false
      return
    }
    if (commitDone || !localPlayer || localPlayer.isEliminated) return
    if (!commitNudgedRef.current) {
      commitNudgedRef.current = true
      toast.warning('Set your Shield Password now — the game begins once every player commits.', { duration: 10_000 })
    }
    const remainingMs = startCommitEndsAt - now
    if (remainingMs > 0 && remainingMs < 60_000 && !commitUrgentNudgedRef.current) {
      commitUrgentNudgedRef.current = true
      toast.error('⏳ Under a minute left to set your Shield Password!', { duration: 10_000 })
    }
  }, [room?.status, commitDone, localPlayer, startCommitEndsAt, now])
  const isHost        = !!address && room?.hostAddress?.toLowerCase() === address.toLowerCase()
  // Spectator: wallet connected but address not in players list (late viewer)
  const isSpectator   = !!address && !!room && room.status === 'active' && !room.players.some(p => p.walletAddress.toLowerCase() === address.toLowerCase())
  // Infection is included: it has no countdown (resolves on-chain in seconds),
  // so without polling the UI would sit on INFECTION until a — sometimes
  // dropped — phase_changed socket event arrives, eating the discussion window.
  const isPhaseSyncing = room?.status === 'active'
    && (phase === 'infection' || phase === 'discussion' || phase === 'voting' || phase === 'reveal')
    && headerCountdownMs <= 0
  const headerTitle = getHeaderTitle(isLoading, round, room?.status)
  const phaseCardDescription = getPhaseDescription(phase, hasVoted, result, room?.status, localPlayer?.status === 'infected')
  const phaseCardBackground = getPhaseCardBackground(phase, room?.status)
  const narrative = useNarrative()
  const narrativeRef = useRef(narrative)
  useEffect(() => { narrativeRef.current = narrative }, [narrative])
  const phaseLabel = getPhaseLabel(narrative, phase, room?.status)
  const phaseColor = getPhaseColor(narrative, phase, room?.status)
  const phaseBadgeText = getPhaseBadgeText(narrative, phase, room?.status)
  const synchronizedFeed = [
    `System sync: Round ${round > 0 ? round : '-'} · ${phaseLabel} · Infected ${infectedCount}/${Math.max(activePlayers.length, 1)}`,
    ...feed,
  ].slice(0, 50)
  const hostPlayerCountLabel = `${totalPlayers} player${totalPlayers === 1 ? '' : 's'} in room. Start when ready.`
  const votePanelLabel = getVotePanelLabel(phase, hasVoted)
  let potPerWinnerWei = 0n
  if (result?.potPerWinner && result.potPerWinner > 0n) {
    potPerWinnerWei = result.potPerWinner
  } else if (result && result.winners.length > 0) {
    potPerWinnerWei = result.totalPot / BigInt(result.winners.length)
  }
  const potPerWinnerValue = Number(potPerWinnerWei) / 1e18
  // Reuse formatToken so tiny stakes don't collapse to "0.0000" — it picks up
  // to 6 decimals for sub-1 amounts, trims trailing zeros, and guards dust.
  const potPerWinnerDisplay = formatToken(potPerWinnerWei)
  const winnerNames = result?.winners.map(w => {
    const p = room?.players?.find(pl => pl.walletAddress.toLowerCase() === w.toLowerCase())
    return p?.displayName ?? `${w.slice(0, 6)}…`
  }) ?? []
  let gameOutcome: GameOutcome = 'draw'
  if (result?.outcome === 'clean_win' || result?.outcome === 'infected_win') gameOutcome = result.outcome
  // Round 0 with a result = the room ended before the game ever started
  // (expired waiting for players / host never started). Whatever outcome the
  // backend reports for the refund, showing "Clean Win" would mislead — use
  // the dedicated aborted card instead.
  if (round === 0) gameOutcome = 'aborted'
  // Red-pulse the vignette during the local player's final voting seconds.
  const votingUrgent = phase === 'voting' && canVote && msLeft > 0 && msLeft <= 15_000
  let playersPanelBody: React.ReactNode
  if (isLoading) {
    playersPanelBody = <p className="text-center font-mono text-xs" style={{ color: '#7d9a72' }}>Loading players…</p>
  } else if (room?.players?.length) {
    playersPanelBody = (
      <PlayersGrid
        players={room.players}
        localAddress={address}
        canVote={canVote}
        selectedVote={selectedVote}
        myVotedTarget={phase === 'voting' ? myVotedTarget : null}
        sweepSeed={sweep?.seed ?? null}
        sweepAnchor={sweep?.anchor ?? 0}
        onToggleVote={(addr) => setSelectedVote(addr === selectedVote ? null : addr)}
      />
    )
  } else {
    playersPanelBody = <p className="text-center font-mono text-xs" style={{ color: '#7d9a72' }}>Waiting for players…</p>
  }

  // Mobile tab visibility helper — on desktop everything shows
  const showOnTab = (tab: GameTab) => !isMobile || activeTab === tab

  // ── Soundscape ───────────────────────────────────────────────────────────
  const { muted } = useSound()
  const soundScene = room?.status === 'ended' ? 'ended' : phase
  useSoundscape(soundScene, muted)
  // Thumping heartbeat overlay during the local player's final voting seconds,
  // synced with the red "lub-dub" vignette pulse (votingUrgent drives both).
  useHeartbeat(votingUrgent, muted)

  // ── Private infection reveal ──────────────────────────────────────────────
  // Fire the "YOU ARE INFECTED" moment only on a LIVE clean→infected flip.
  // The first observed status is treated as baseline so a page refresh while
  // already infected doesn't replay the reveal.
  const prevMyStatusRef = useRef<string | null>(null)
  useEffect(() => {
    const status = localPlayer?.status ?? null
    if (status === null) return
    const prev = prevMyStatusRef.current
    prevMyStatusRef.current = status
    if (prev === null || prev === status) return
    if (status === 'infected') {
      setMoment({
        key: `infected:${roomId}:${Date.now()}`,
        data: {
          label: narrativeRef.current.moments.infected.label,
          color: narrativeRef.current.phaseColors.infection,
          glyph: '☣',
          sublabel: narrativeRef.current.moments.infected.sublabel,
          intense: true,
        },
      })
      playSting(GAME_OVER_TRACKS.infected, muted, 0.35)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localPlayer?.status])
  // Re-baseline when switching rooms.
  useEffect(() => { prevMyStatusRef.current = null }, [roomId])

  // ── Round-opening containment sweep (LATCHED) ────────────────────────────
  // Seeded from room+round ONLY — deliberately uncorrelated with who is
  // actually infected. lib/containment-sweep.ts explains why that is a safety
  // property rather than a style choice.
  //
  // ⚠ The latch is the whole point. Infection is a TRANSIENT phase: the backend
  // calls assignInfection and the room flips to Discussion within seconds (note
  // that computePhaseEndsAtMs gives phase 0 no duration at all). Deriving the
  // seed straight from `phase === 'infection'` therefore yanked the beat off
  // screen the moment the chain moved on, so the sweep died halfway across the
  // board. Latch on entry instead and hold for the beat's own duration; the
  // overlay is non-blocking, so letting it outlive the phase costs nothing.
  //
  // The ANCHOR is latched alongside the seed for the same reason. It is the
  // chain's phaseStartedAt, which is re-stamped on every phase change — so
  // reading it live meant that the instant Infection gave way to Discussion the
  // anchor jumped forward, the sweep's elapsed time went negative, and the beat
  // restarted from seat one on top of the new phase.
  useEffect(() => {
    if (!sweepArmed) return
    // The anchor is CHAIN time, but the grid measures elapsed against the
    // browser's Date.now(). A client whose clock disagrees with the chain by
    // more than the beat's own length would otherwise never see the sweep at
    // all — running slow makes elapsed negative, running fast makes the beat
    // look already finished. Clamping into the only window it can occupy
    // degrades a skewed client to "plays, slightly out of step with the table"
    // rather than "silently plays nothing".
    const now = Date.now()
    const total = sweepDurationMs(room?.players?.length ?? 0)
    const chainAnchor = currentRound?.startedAt ?? now
    setSweep({ seed: sweepArmed, anchor: Math.min(Math.max(chainAnchor, now - total), now) })
    const t = setTimeout(() => setSweep(null), total)
    return () => clearTimeout(t)
  // Depends on the armed seed only: re-running as the roster reference or the
  // phase clock churns would restart the beat mid-sweep.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sweepArmed])
  useEffect(() => { setSweep(null) }, [roomId])

  // Reset optimistic vote when round changes
  const roundNumber = currentRound?.number ?? 0
  useEffect(() => {
    setOptimisticVotedFor(null)
    setOptimisticProofDone(false)
  }, [roundNumber])

  // Avoid carrying optimistic commitment state into another room.
  useEffect(() => {
    setOptimisticCommitDone(false)
    setOptimisticProofDone(false)
    setGameOverDismissed(false)
  }, [roomId])

  // If the room ended but live socket events were missed (result is still null),
  // do a chain read so the game-over overlay is populated correctly.
  useEffect(() => {
    if (room?.status === 'ended' && !result) refresh()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.status])

  // While the timer has elapsed but the next phase hasn't landed yet, poll the
  // chain every 4s so a slow tx / RPC lag eventually unsticks the UI without
  // requiring the user to refresh manually. If polite refreshes haven't fixed
  // it after 15s, force a socket reconnect once — rejoin makes the backend
  // re-send its authoritative room_state snapshot, recovering from a socket
  // that silently stopped delivering events.
  const [syncWaitMs, setSyncWaitMs] = useState(0)
  const socketKickedRef = useRef(false)
  const forceResync = useCallback(() => {
    refresh()
    if (socket) {
      socket.disconnect()
      socket.connect() // 'connect' handler re-emits join_room → fresh room_state
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, refresh])
  useEffect(() => {
    if (!isPhaseSyncing) {
      setSyncWaitMs(0)
      socketKickedRef.current = false
      return
    }
    const start = Date.now()
    setSyncWaitMs(0)
    const tick = () => {
      const waited = Date.now() - start
      setSyncWaitMs(waited)
      refresh()
      socket?.emit('request_room_refresh', { roomId })
      if (waited >= 15_000 && !socketKickedRef.current) {
        socketKickedRef.current = true
        forceResync()
      }
    }
    tick()
    const id = setInterval(tick, 4_000)
    return () => clearInterval(id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhaseSyncing, roomId])

  // Play game-over sting once when result arrives
  useEffect(() => {
    if (!result) return
    const src = result.outcome === 'infected_win'
      ? GAME_OVER_TRACKS.infected
      : GAME_OVER_TRACKS.clean
    playSting(src, muted, 0.55)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.outcome])

  // ── Chat socket listeners ─────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return
    const msgHandler = (msg: { sender: string; displayName: string; message: string; timestamp: number }) => {
      setChatMessages(prev => [...prev.slice(-199), msg])
      // Increment unread if not viewing chat tab on mobile
      if (isMobileRef.current && activeTabRef.current !== 'chat') {
        setUnreadChat(prev => prev + 1)
      }
    }
    const historyHandler = (history: { sender: string; displayName: string; message: string; timestamp: number }[]) => {
      setChatMessages(history.slice(-200))
    }
    const errorHandler = (data: { message?: string }) => {
      if (data?.message) toast.error(data.message)
    }
    socket.on('chat_message', msgHandler)
    socket.on('chat_history', historyHandler)
    socket.on('chat_error', errorHandler)
    return () => {
      socket.off('chat_message', msgHandler)
      socket.off('chat_history', historyHandler)
      socket.off('chat_error', errorHandler)
    }
  }, [socket])

  // Auto-scroll chat to bottom on new messages.
  //
  // Deliberately scrolls the log container itself rather than calling
  // scrollIntoView on a sentinel: scrollIntoView walks up and scrolls EVERY
  // scrollable ancestor, including the window, so each incoming message used to
  // jerk the whole page toward the chat panel mid-game. Setting scrollTop only
  // moves this one element.
  //
  // Held back when the reader has scrolled up, so a message landing while
  // they're reading history doesn't drag them back down.
  useEffect(() => {
    const el = chatScrollRef.current
    if (!el || !chatPinnedRef.current) return
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [chatMessages])

  const handleSendChat = useCallback(() => {
    if (!chatInput.trim() || !socket || !address || !roomId || !canChat) return
    // Screened here for an instant answer; the server screens again on receipt
    // and is the copy that actually decides. The input is left intact on
    // rejection so the sender can edit rather than retype.
    const screened = checkChatMessage(chatInput)
    if (!screened.ok) {
      toast.error(screened.reason)
      return
    }
    const displayName = room?.players?.find(p => p.walletAddress.toLowerCase() === address.toLowerCase())?.displayName
      ?? `${address.slice(0, 6)}…${address.slice(-4)}`
    socket.emit('chat_message', { roomId, message: screened.text, playerAddress: address, displayName })
    setChatInput('')
    noteSent()
  }, [chatInput, socket, address, roomId, room, canChat, noteSent])

  // ── Vote tally ────────────────────────────────────────────────────────────
  const voteTally: Record<string, number> = {}
  currentRound?.votes.forEach(v => {
    voteTally[v.targetAddress] = (voteTally[v.targetAddress] ?? 0) + 1
  })

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCastVote = useCallback(async () => {
    if (!selectedVote || !address || !roomId) return
    const client = getContractClient()
    if (!client) return
    setVoting(true)
    setVoteError(null)
    try {
      // Same dropped-request retry as the Shield. A vote lost to a flaky node is
      // worse than a Shield lost to one: an absent vote is counted against you.
      await retryOnTransient(() =>
        client.castVote(address, BigInt(roomId), selectedVote as `0x${string}`))
      // Optimistic update — flip the UI immediately without waiting for the socket broadcast.
      setOptimisticVotedFor(selectedVote)
      setSelectedVote(null)
      socket?.emit('request_room_refresh', { roomId })
      toast.success('Vote submitted!')
      refresh()
    } catch (err) {
      const msg = parseContractError(err)
      setVoteError(msg)
      toast.error(msg)
    } finally {
      setVoting(false)
    }
  }, [selectedVote, address, roomId, refresh, socket])

  const handleSubmitProof = useCallback(async () => {
    if (!address || !roomId || !secretPhrase) {
      setProofError('Enter your secret phrase to generate a proof.')
      return
    }
    setProving(true)
    setProofError(null)
    try {
      const client = getContractClient()
      if (!client) throw new Error('Contract not configured.')

      // Use on-chain roleCommitment stored when player submitted their role.
      const commitment = (localPlayer?.roleCommitment ?? '') as `0x${string}`
      if (!commitment) throw new Error('Role commitment not found. Submit your role first.')

      const { proveInnocence, computeInnocenceNullifier, deriveSecret } = await import('@/lib/zk')
      const secretBigInt = await deriveSecret(secretPhrase)
      const nullifier = await computeInnocenceNullifier(secretBigInt, BigInt(roomId), BigInt(round))
      const proof = await proveInnocence({
        role:        'clean',
        secret:      secretBigInt,
        roomId:      BigInt(roomId),
        roundNumber: BigInt(round),
        commitment,
      })

      const nullifierHex: `0x${string}` = `0x${nullifier.toString(16).padStart(64, '0')}`
      const proofBytes   = ('0x' + proof.proof.map(b => b.toString(16).padStart(2, '0')).join('')) as `0x${string}`

      // Notify backend before writing on-chain — backend validates and may send proof_error
      if (socket) {
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, 3000) // fall through if no ack within 3s
          socket.once('proof_error', (data: { message?: string }) => {
            clearTimeout(timer)
            reject(new Error(data.message ?? 'Backend rejected proof.'))
          })
          socket.once('proof_ack', () => {
            clearTimeout(timer)
            resolve()
          })
          socket.emit('submit_proof', {
            roomId,
            playerAddress: address,
            commitment,
            nullifier: nullifierHex,
            zkProof: proofBytes,
            isFreeProof: !localPlayer?.freeProofUsed,
          })
        })
      }

      // Approve exact proof fee (not stakeAmount) for paid proofs
      if (localPlayer?.freeProofUsed && chainId && CUSD_ADDRESSES[chainId]) {
        const feeAmt = room?.proofFee ?? 0n
        if (feeAmt > 0n) {
          await client.approveCUSD(address, CUSD_ADDRESSES[chainId], feeAmt)
        }
      }

      // Retried on a dropped request, but NOT the proof generation above — that
      // is seconds of work and the Shield window is on a clock.
      await retryOnTransient(() =>
        client.submitInnocenceProof(address, BigInt(roomId), commitment, nullifierHex, proofBytes))
      setOptimisticProofDone(true)
      socket?.emit('request_room_refresh', { roomId })
      toast.success('Shield activated!')
      setMoment({
        key: `shield:${roomId}:${round}`,
        data: {
          label: narrativeRef.current.moments.shield.label,
          color: narrativeRef.current.phaseColors.discussion,
          glyph: '✚',
          sublabel: `Round ${round} — ${narrativeRef.current.moments.shield.sublabel}`,
        },
      })
      schedulePostTxRefresh()
    } catch (err) {
      const msg = parseContractError(err)
      if (msg.includes('AlreadyProvedThisRound')) {
        setOptimisticProofDone(true)
        socket?.emit('request_room_refresh', { roomId })
        schedulePostTxRefresh()
      } else {
        setProofError(msg)
        toast.error(msg)
      }
    } finally {
      setProving(false)
    }
  }, [address, roomId, secretPhrase, round, localPlayer, room, chainId, socket, schedulePostTxRefresh])

  const handleCommitRole = useCallback(async () => {
    if (!address || !roomId || !secretPhrase) {
      setCommitError('Enter a secret phrase to commit your role.')
      return
    }
    setCommitting(true)
    setCommitError(null)
    try {
      const { generateRoleCommitment, proveRoleCommitment, deriveSecret } = await import('@/lib/zk')
      const role = 'clean' as const // backend assigns infection via assignInfection
      const secretBigInt = await deriveSecret(secretPhrase)
      const { commitment } = await generateRoleCommitment(role, secretBigInt)
      const commitmentHex = ensureHexPrefixed(commitment)

      // Pre-submit duplicate guard. Two players using the same passphrase produce
      // the same commitment (poseidon2(0, secret)) and would collide nullifiers
      // at Shield activation, so only one of them could ever submit.
      //
      // Path 1 (preferred): backend reservation endpoint. Zero gas on reject.
      // Path 2 (fallback):  client-side check + confirm, used when backend is
      //                     unreachable. The contract still enforces uniqueness
      //                     as the final guard if both layers miss.
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? 'http://localhost:4000'
      let reservedByBackend = false
      try {
        const reserveRes = await fetch(`${backendUrl}/api/rooms/${roomId}/reserve-commitment`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ commitment: commitmentHex, address }),
        })
        if (reserveRes.status === 409) {
          const body = await reserveRes.json().catch(() => ({})) as { message?: string }
          const msg = body.message ?? 'That Shield Password is already taken in this room. Pick a different one.'
          setCommitError(msg)
          setCommitting(false)
          return
        }
        if (reserveRes.ok) reservedByBackend = true
      } catch {
        // backend unreachable — fall through to client-side fallback
      }

      if (!reservedByBackend) {
        const myAddrLower = address.toLowerCase()
        const collidesWith = room?.players?.find(p =>
          p.walletAddress.toLowerCase() !== myAddrLower &&
          p.roleCommitment &&
          p.roleCommitment.toLowerCase() === commitmentHex.toLowerCase()
        )
        if (collidesWith) {
          const proceed = window.confirm(
            `Heads up: another player has already set the same Shield Password (${collidesWith.displayName}).\n\n` +
            `The contract will reject your submission. Pick a different password — or press OK to try anyway (the transaction will revert and you'll still pay the network fee).`
          )
          if (!proceed) {
            setCommitError('Choose a different Shield Password.')
            setCommitting(false)
            return
          }
        }
      }

      const proofResult = await proveRoleCommitment({ role, secret: secretBigInt, commitment })
      const proofBytes    = ('0x' + proofResult.proof.map((b: number) => b.toString(16).padStart(2, '0')).join('')) as `0x${string}`
      const client = getContractClient()!
      await client.submitRoleCommitment(address, BigInt(roomId), commitmentHex, proofBytes)
      setOptimisticCommitDone(true)
      setSecretPhrase('')
      socket?.emit('request_room_refresh', { roomId })
      toast.success('Shield Password set!')
      schedulePostTxRefresh()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Role commitment failed.'
      // AlreadyCommitted means a previous submission went through — treat as success
      if (msg.includes('AlreadyCommitted')) {
        setOptimisticCommitDone(true)
        setSecretPhrase('')
        socket?.emit('request_room_refresh', { roomId })
        schedulePostTxRefresh()
      } else {
        setCommitError(msg.split('\n')[0])
      }
    } finally {
      setCommitting(false)
    }
  }, [address, roomId, secretPhrase, schedulePostTxRefresh, socket, room])

  const handleStartGame = useCallback(async () => {
    if (!address || !roomId) return
    const client = getContractClient()
    if (!client) return
    setStarting(true)
    setStartError(null)
    try {
      await retryOnTransient(() => client.startGame(address, BigInt(roomId)))
      socket?.emit('request_room_refresh', { roomId })
      schedulePostTxRefresh()
      // `starting` is deliberately NOT cleared here.
      //
      // The transaction is mined by this point, but the room is still `waiting`
      // until the backend observes it — several seconds on a slow read. Clearing
      // the flag would put a live "Start Game" button back in front of the host
      // during that gap, and pressing it again just reverts. This whole block is
      // gated on `status === 'waiting'`, so it unmounts itself the moment the
      // room actually starts.
    } catch (err) {
      const msg = parseContractError(err)
      // Keep the revert name if there is one (e.g. NotEnoughPlayers).
      const revert = (/Error:\s*(\w+\(\))/).exec(msg)?.[1] ?? msg.split('\n')[0]
      toast.error(`Start failed: ${revert}`)
      setStartError(revert)
      setStarting(false)
    }
  }, [address, roomId, schedulePostTxRefresh, socket])

  // ── Phase advance ticker — accelerates when timer is nearly expired ─────
  const nearPhaseEnd = room?.status === 'active' && headerCountdownMs <= 8_000 && headerCountdownMs > 0
  useEffect(() => {
    if (!socket || !roomId || room?.status !== 'active') return
    // Tick faster (1 s) in the last 8 seconds so transition feels instant.
    const interval = nearPhaseEnd ? 1_000 : 2_000
    const id = setInterval(() => {
      socket.emit('request_phase_advance', { roomId })
    }, interval)
    return () => clearInterval(id)
  }, [socket, roomId, room?.status, nearPhaseEnd])

  // When the local timer reaches zero, proactively request phase advancement
  // once per round+phase to avoid UI stalling until the next monitor tick.
  useEffect(() => {
    if (!socket || !roomId || room?.status !== 'active') return
    const key = `${round}:${phase}`
    if (headerCountdownMs > 0) {
      if (phaseAdvanceNudgeKeyRef.current === key) {
        phaseAdvanceNudgeKeyRef.current = ''
      }
      return
    }
    if (phaseAdvanceNudgeKeyRef.current === key) return
    if (phase !== 'infection' && phase !== 'discussion' && phase !== 'voting' && phase !== 'reveal') return

    phaseAdvanceNudgeKeyRef.current = key
    socket.emit('request_phase_advance', { roomId })
    socket.emit('request_room_refresh', { roomId })
    // Burst refresh: emit again after short delays to ensure propagation.
    const t1 = setTimeout(() => { socket.emit('request_phase_advance', { roomId }); socket.emit('request_room_refresh', { roomId }) }, 1_500)
    const t2 = setTimeout(() => { socket.emit('request_phase_advance', { roomId }); socket.emit('request_room_refresh', { roomId }) }, 3_500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [socket, roomId, room?.status, headerCountdownMs, phase, round])

  // ── Periodic sync for waiting/starting rooms (pick up game-start quickly) ─
  useEffect(() => {
    if (!socket || !roomId) return
    const status = room?.status
    if (status !== 'waiting' && status !== 'starting') return
    const id = setInterval(() => {
      socket.emit('request_room_refresh', { roomId })
    }, 2_500)
    return () => clearInterval(id)
  }, [socket, roomId, room?.status])

  // ── Periodic full refresh — only when socket is offline ─────────────────
  useEffect(() => {
    if (!roomId || socketOn) return
    const id = setInterval(() => { refresh() }, 5_000)
    return () => clearInterval(id)
  }, [roomId, socketOn, refresh])

  // ── No roomId → the arena hub, not a dead end ───────────────────────────
  // The "Match" nav item links here with no room param, so this is the landing
  // state for anyone who clicks the tab rather than following a room link. It
  // used to read "No room specified." even mid-game; ArenaHub resolves the
  // player's own room, offers live matches to spectate, or shows the hub.
  if (!roomId) {
    return <ArenaHub />
  }

  // ── Room not found / initializing guard ─────────────────────────────────
  if (!isLoading && !room && error === 'Room not found.') {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6" style={{ backgroundColor: '#060b06', color: '#d4c9b2', backgroundImage: 'url(/images/bg-game.webp)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="fixed inset-0 pointer-events-none" style={{ backgroundColor: 'rgba(6,11,6,0.88)' }} />
        <div className="relative flex flex-col items-center gap-4 text-center px-8">
          <p className="font-display text-4xl" style={{ color: '#e63329' }}>Room Not Found</p>
          <p className="font-mono text-sm max-w-sm" style={{ color: '#8fa882' }}>
            The room may still be initializing on-chain, or the link may be incorrect. You can return to the lobby and join from there.
          </p>
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => refresh()}
              className="rounded border px-5 py-2 font-mono text-sm uppercase tracking-wider transition-all hover:opacity-80"
              style={{ borderColor: '#6b8e23', color: '#6b8e23' }}
            >
              Retry
            </button>
            <button onClick={() => router.push('/lobby')} className="rounded border px-5 py-2 font-mono text-sm uppercase tracking-wider transition-all hover:opacity-80" style={{ borderColor: 'rgba(212,201,178,0.4)', color: '#d4c9b2' }}>
              ← Back to Lobby
            </button>
          </div>
        </div>
      </main>
    )
  }

  // ── Set Shield Password card ────────────────────────────────────────────
  // Rendered once, at the top of the telemetry strip (see its slot below).
  /**
   * Vote Panel, defined once and rendered in two slots.
   *
   * It lived in the right-hand `<aside>`, a different flex container from the
   * left column that holds the board. On a phone the grid collapses to one
   * column and the whole aside renders after everything in the left column, so
   * the panel sat below the cam no matter what `order` it was given — order only
   * sorts siblings, and these were never siblings.
   *
   * So the phone renders it from the LEFT column at order-3, directly above the
   * cam, and desktop keeps it in the sidebar where there is room for it.
   */
  const votePanel = showOnTab('play') ? (
              <div className="rise-in order-3 md:order-none rounded-lg border p-6" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(230,51,41,0.25)', animationDelay: '80ms' }}>
                <h3 className="font-heading text-xl leading-none" style={{ color: '#d4c9b2' }}>Vote Panel</h3>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.16em]" style={{ color: '#7d9a72' }}>{votePanelLabel}</p>

                {phase === 'voting' && hasVoted && (
                  <div className="mt-4 rounded-lg border p-3" style={{ borderColor: 'rgba(107,142,35,0.3)', backgroundColor: 'rgba(107,142,35,0.06)' }}>
                    <p className="font-mono text-xs" style={{ color: '#6b8e23' }}>✓ Your vote has been recorded.</p>
                    {myVotedTarget && (
                      <p className="mt-1 font-mono text-xs" style={{ color: '#8fa882' }}>
                        You voted against{' '}
                        <span style={{ color: '#f5c518' }}>
                          {room?.players?.find(p => p.walletAddress.toLowerCase() === myVotedTarget.toLowerCase())?.displayName ?? `${myVotedTarget.slice(0, 6)}…`}
                        </span>
                      </p>
                    )}
                  </div>
                )}

                {phase === 'voting' && canVote && (
                  <div className="mt-4 space-y-2">
                    {activePlayers
                      .filter(p => p.walletAddress !== address)
                      .map((p) => (
                        <button
                          key={p.walletAddress}
                          onClick={() => setSelectedVote(p.walletAddress === selectedVote ? null : p.walletAddress)}
                          className="flex w-full items-center justify-between rounded-lg border px-4 py-3 font-mono text-sm uppercase tracking-[0.12em] transition-all hover:opacity-90"
                          style={{
                            borderColor: selectedVote === p.walletAddress ? '#f5c518' : 'rgba(230,51,41,0.35)',
                            backgroundColor: selectedVote === p.walletAddress ? 'rgba(245,197,24,0.1)' : 'rgba(230,51,41,0.1)',
                            color: '#d4c9b2',
                          }}
                        >
                          <span>{p.displayName}</span>
                          {voteTally[p.walletAddress] ? (
                            <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: 'rgba(230,51,41,0.25)', color: '#ff6b6b' }}>
                              {voteTally[p.walletAddress]}
                            </span>
                          ) : null}
                        </button>
                      ))}
                    {voteError && <p className="font-mono text-xs" style={{ color: '#e63329' }}>{voteError}</p>}
                    <button
                      onClick={handleCastVote}
                      disabled={!selectedVote || voting || !canVote}
                      className="mt-2 w-full rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
                      style={{ backgroundColor: '#e63329', borderColor: '#e63329', color: '#d4c9b2' }}
                    >
                      {voting ? 'Submitting…' : 'Cast Vote'}
                    </button>
                  </div>
                )}

                {phase === 'voting' && !canVote && !hasVoted && (
                  <p className="mt-4 font-mono text-xs" style={{ color: '#8fa882' }}>Voting is available only to alive room participants.</p>
                )}

                {phase !== 'voting' && currentRound?.votes.length ? (
                  <div className="mt-4 space-y-1">
                    {Object.entries(voteTally)
                      .sort(([, a], [, b]) => b - a)
                      .map(([addr, cnt]) => {
                        const name = room?.players?.find(p => p.walletAddress.toLowerCase() === addr.toLowerCase())?.displayName ?? `${addr.slice(0, 8)}…`
                        return (
                          <div key={addr} className="flex justify-between font-mono text-xs" style={{ color: '#8fa882' }}>
                            <span>{name}</span>
                            <span style={{ color: '#ff6b6b' }}>{cnt} vote{cnt === 1 ? '' : 's'}</span>
                          </div>
                        )
                      })}
                  </div>
                ) : null}
              </div>
  ) : null

  const shieldCommitCard = room?.status === 'starting' && showOnTab('play') ? (
    <div className={commitDone ? undefined : 'toxic-pulse rounded-lg'}>
      <div
        className="rise-in rounded-lg border p-5"
        style={commitDone
          ? { borderColor: 'rgba(107,142,35,0.35)', backgroundColor: 'rgba(107,142,35,0.08)' }
          : { borderColor: 'rgba(245,197,24,0.7)', backgroundColor: 'rgba(245,197,24,0.08)', boxShadow: '0 0 18px rgba(245,197,24,0.25)' }}
      >
        <div className="flex items-center justify-between gap-3">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.2em]" style={{ color: commitDone ? '#6b8e23' : '#f5c518' }}>
            {commitDone ? 'Shield Password' : '⚠ Action required — Set Shield Password'}
          </p>
          {!commitDone && (
            <span className="font-heading text-2xl leading-none tabular-nums" style={{ color: startCommitEndsAt - now < 60_000 ? '#e63329' : '#f5c518' }}>
              {formatCountdown(Math.max(0, startCommitEndsAt - now))}
            </span>
          )}
        </div>
        {commitDone ? (
          <p className="mt-3 font-mono text-xs" style={{ color: '#6b8e23' }}>✓ Shield Password set. Waiting for all players…</p>
        ) : (
          <>
            <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>
              Enter your Shield Password before the timer runs out. Keep it secret — you&apos;ll need it to activate your Shield later.
            </p>
            <PasswordInput
              placeholder="My Shield Password…"
              value={secretPhrase}
              onChange={setSecretPhrase}
              borderColor="rgba(245,197,24,0.5)"
              className="mt-3"
            />
            {commitError && <p className="mt-2 font-mono text-xs" style={{ color: '#e63329' }}>{commitError}</p>}
            <button
              onClick={handleCommitRole}
              disabled={!canCommit || !secretPhrase || committing}
              className="mt-3 w-full rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: '#f5c518', borderColor: '#f5c518', color: '#060b06' }}
            >
              {committing ? 'Registering Shield Password…' : 'Set Shield Password'}
            </button>
          </>
        )}
      </div>
    </div>
  ) : null

  return (
    <main className="min-h-screen" style={{ backgroundColor: '#060b06', color: '#d4c9b2', backgroundImage: 'url(/images/bg-game.webp)', backgroundSize: 'cover', backgroundPosition: 'center top', backgroundAttachment: 'fixed' }}>
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundColor: 'rgba(6,11,6,0.88)', zIndex: 0 }} />
      <ArenaDoors roomId={roomId} />
      <AmbientLayer urgent={votingUrgent} />
      <PhaseTransition
        phaseKey={`${round}:${phase}`}
        label={narrative.phaseLabels[phase]}
        color={narrative.phaseColors[phase]}
        sublabel={round > 0 ? `Round ${round}` : undefined}
        glyphKey={phase}
        enabled={room?.status === 'active' && phase !== 'ended'}
      />
      <MomentOverlay momentKey={moment?.key ?? null} moment={moment?.data ?? null} />
      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 pt-3">
        <ModifierBanner roomId={roomId} round={round} />
      </div>
      {/* Same coach marks the demo uses, and the same localStorage keys, so a
          player who learned Shield timing in the demo is not taught it twice.
          Gated on an active room: hints during setup would fire with nothing
          on screen to apply them to. */}
      <PhaseCoach phase={phase} enabled={room?.status === 'active' && phase !== 'ended'} />
      {result && !gameOverDismissed && (
        /* Aborted = refund, not winnings — a "pot per winner" counter and a
           winners roll-call would read as a result that never happened. */
        <GameOverOverlay
          outcome={gameOutcome}
          potPerWinner={gameOutcome === 'aborted' ? 0 : potPerWinnerValue}
          winners={gameOutcome === 'aborted' ? [] : winnerNames}
          progress={gameOutcome === 'aborted' ? null : playerProgress}
          onDismiss={() => setGameOverDismissed(true)}
        />
      )}
      <div className="relative game-tab-content" style={{ zIndex: 1 }}>
      {/* Nav */}
      <div className="sticky top-0 z-50 px-4 pt-4 sm:px-8 sm:pt-6">
        <div className="mx-auto w-full max-w-6xl">
          <SiteNav currentPath="/game" />
        </div>
      </div>

      {/* Game Header + Telemetry — visible on Game tab (mobile) or always (desktop) */}
      {showOnTab('play') && (
      <>
      <header
        className="relative overflow-hidden px-6 py-12"
        style={{ borderBottom: '1px solid rgba(107,142,35,0.2)' }}
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute left-1/4 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: '#6b8e23' }} />
          <div className="absolute right-1/3 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: '#cc1414' }} />
        </div>

        <div className="relative mx-auto w-full max-w-6xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Room name — prominent display */}
            <div className="flex flex-col gap-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: '#7d9a72' }}>Room</p>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="font-heading text-lg sm:text-2xl leading-none"
                  style={{ color: '#e63329', textShadow: '0 0 12px rgba(230,51,41,0.4)' }}
                >
                  {room?.name ? room.name : quarantineCode(BigInt(roomId))}
                </span>
                {isHost && !room?.name && room?.status !== 'ended' && (
                  <button
                    onClick={() => setRoomNameEditing(true)}
                    className="rounded border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest transition-all hover:brightness-125 active:scale-95"
                    style={{ borderColor: 'rgba(107,142,35,0.5)', color: '#6b8e23', backgroundColor: 'rgba(107,142,35,0.08)' }}
                  >
                    + Set name
                  </button>
                )}
              </div>
              {room?.name && (
                <span className="font-mono text-[10px]" style={{ color: '#7d9a72' }}>{quarantineCode(BigInt(roomId))}</span>
              )}
              {roomNameEditing && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault()
                    const trimmed = pendingRoomName.trim()
                    if (!trimmed) { setRoomNameEditing(false); return }
                    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? 'http://localhost:4000'
                    try {
                      const res = await fetch(`${backendUrl}/api/rooms/${roomId}/name`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: trimmed }),
                      })
                      if (res.status === 409) {
                        toast.error(`"${trimmed}" is already taken by another active room.`)
                        return
                      }
                      if (!res.ok) {
                        toast.error(`Could not save room name. (${res.status})`)
                        return
                      }
                      setRoomNameEditing(false)
                      setPendingRoomName('')
                      refresh()
                    } catch (err) {
                      toast.error(`Failed to save name: ${err instanceof Error ? err.message : String(err)}`)
                    }
                  }}
                  className="mt-1 flex items-center gap-2"
                >
                  <input
                    autoFocus
                    type="text"
                    maxLength={40}
                    placeholder="Room name"
                    value={pendingRoomName}
                    onChange={(e) => setPendingRoomName(e.target.value)}
                    className="rounded-lg border bg-transparent px-3 py-1.5 font-mono text-xs focus:outline-none"
                    style={{ borderColor: 'rgba(107,142,35,0.4)', color: '#d4c9b2' }}
                  />
                  <button
                    type="submit"
                    className="rounded border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-all hover:brightness-125 active:scale-95"
                    style={{ borderColor: '#6b8e23', color: '#6b8e23', backgroundColor: 'rgba(107,142,35,0.12)' }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setRoomNameEditing(false); setPendingRoomName('') }}
                    className="rounded border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-all hover:brightness-125 active:scale-95"
                    style={{ borderColor: '#7d9a72', color: '#7d9a72' }}
                  >
                    Cancel
                  </button>
                </form>
              )}
            </div>
            <span
              className="rounded border px-3 py-1 font-mono text-xs uppercase tracking-[0.2em]"
              style={{ borderColor: `${phaseColor}44`, backgroundColor: `${phaseColor}18`, color: phaseColor }}
            >
              {phaseBadgeText}
            </span>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-6">
            <h1 className="font-display text-4xl font-bold leading-none sm:text-7xl md:text-8xl" style={{ color: '#d4c9b2' }}>
              {headerTitle}
            </h1>
            {headerCountdownMs > 0 && (
              <div
                className="flex flex-col items-center rounded-xl border px-5 py-3 sm:px-10 sm:py-4"
                style={{ borderColor: 'rgba(107,142,35,0.45)', backgroundColor: 'rgba(107,142,35,0.06)' }}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: '#7d9a72' }}>Time Left</p>
                <p className="mt-1 font-mono text-3xl sm:text-6xl font-bold leading-none tabular-nums" style={{ color: '#6b8e23', textShadow: '0 0 20px rgba(107,142,35,0.5)' }}>
                  {formatCountdown(headerCountdownMs)}
                </p>
              </div>
            )}
          </div>

          {isPhaseSyncing && (
            <div
              className="mt-4 inline-flex flex-wrap items-center gap-3 rounded border px-3 py-2 font-mono text-xs uppercase tracking-[0.16em]"
              style={{ borderColor: 'rgba(245,197,24,0.4)', backgroundColor: 'rgba(245,197,24,0.08)', color: '#f5c518' }}
            >
              <span>Syncing next phase on-chain{'.'.repeat(1 + Math.floor((syncWaitMs / 500) % 3))}</span>
              {syncWaitMs >= 12_000 && (
                <button
                  onClick={forceResync}
                  className="rounded border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-all hover:brightness-125 active:scale-95"
                  style={{ borderColor: '#f5c518', color: '#f5c518', backgroundColor: 'rgba(245,197,24,0.14)' }}
                >
                  ↺ Force resync
                </button>
              )}
              {syncWaitMs >= 30_000 && (
                <button
                  onClick={() => window.location.reload()}
                  className="rounded border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-all hover:brightness-125 active:scale-95"
                  style={{ borderColor: '#e63329', color: '#e63329', backgroundColor: 'rgba(230,51,41,0.12)' }}
                >
                  ⟳ Reload page
                </button>
              )}
            </div>
          )}

          {/* Connection status + player identity */}
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-block h-2 w-2 rounded-full ${socketOn ? 'bg-green-400' : 'bg-yellow-400'}`} />
              <span className="font-mono text-xs" style={{ color: '#7d9a72' }}>
                {socketOn ? 'Live · backend connected' : 'On-chain read-only'}
              </span>
              <button
                onClick={() => refresh()}
                disabled={isLoading}
                title="Fetch latest state from chain"
                className="ml-1 rounded border px-3 py-1 font-mono text-xs font-bold uppercase tracking-widest transition-all disabled:opacity-40 hover:brightness-125 active:scale-95"
                style={{ borderColor: '#6b8e23', color: '#6b8e23', backgroundColor: 'rgba(107,142,35,0.08)', boxShadow: '0 0 6px rgba(107,142,35,0.35)' }}
              >
                {isLoading ? '…' : '↺ Sync'}
              </button>
              {error && <span className="font-mono text-xs" style={{ color: '#e63329' }}>{error}</span>}
              {/* Prominent player identity badge */}
              {localPlayer && (
                <span
                  className="ml-auto rounded-lg border px-3 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.18em]"
                  style={{
                    borderColor: 'rgba(107,142,35,0.6)',
                    backgroundColor: 'rgba(107,142,35,0.12)',
                    color: '#6b8e23',
                    boxShadow: '0 0 10px rgba(107,142,35,0.2)',
                  }}
                >
                  You: <span style={{ color: '#d4c9b2' }}>{localPlayer.displayName}</span>
                </span>
              )}
            </div>
            <p className="font-mono text-xs font-bold tracking-wide" style={{ color: '#f59e0b' }}>
              <span className="inline-block animate-pulse">↺</span>
              {' Sync periodically to get the latest game state.'}
            </p>
          </div>
        </div>
      </header>

      {/* Telemetry Strip */}
      <div className="px-6 pt-8">
        <div className="mx-auto w-full max-w-6xl">
          {/* Set Shield Password — first thing under the header, immediately
              above POT / INFECTED / SHIELD WINDOW. It is a timed, mandatory
              action, so it sits at the top of the game body rather than in
              the left column, where the Area 51 board pushed it below the
              fold and players missed the commit window entirely. */}
          {shieldCommitCard && <div className="mb-4">{shieldCommitCard}</div>}

          {/* Parity already decided, game not ended yet. PlagueGame evaluates
              `infectedAlive > cleanAlive` only after infection assignment
              (L613), not in finalizeElimination — so a majority reached by
              elimination stands for the rest of Reveal plus two backend txns
              (~15-20s) before the win lands. Without this banner that window
              reads as the rules being broken. */}
          {outbreakDecided && (
            <div
              className="toxic-pulse mb-4 rounded-lg border px-4 py-3 text-center"
              style={{ borderColor: 'rgba(230,51,41,0.6)', backgroundColor: 'rgba(230,51,41,0.1)' }}
            >
              <p className="font-mono text-xs font-bold uppercase tracking-[0.18em]" style={{ color: '#e63329' }}>
                ☣ Outbreak decided — infected hold the majority
              </p>
              <p className="mt-1.5 font-mono text-[11px] leading-relaxed" style={{ color: '#8fa882' }}>
                {infectedCount} infected vs {activePlayers.length - infectedCount} clean. The
                infected have already won — the result is being settled on-chain now.
              </p>
            </div>
          )}
          {/* Pot / infected / shield window.
              ── Phone: one strip. Tablet up: three cards. ────────────────────
              These were three full-width stacked cards on a phone, roughly a
              third of the fold spent before the game itself appeared. They are
              not reading material though — nobody reads their pot, they glance
              at it — so the phone gets one line and taps it open for the
              detail.

              Not moved to a tab, which was the other option considered: hiding
              the stake and the infected count is how you end up with players
              who never learn the money is real, and this session already
              established that panels behind tabs do not get looked at. */}
          <button
            type="button"
            onClick={() => setStatsOpen(o => !o)}
            aria-expanded={statsOpen}
            className="rise-in w-full rounded-lg border px-3 py-2.5 md:hidden"
            style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.25)' }}
          >
            <span className="flex items-center justify-between gap-2 font-mono text-[11px] uppercase tracking-wider" style={{ color: '#7d9a72' }}>
              <span>POT <b style={{ color: '#f5c518' }}>{potCUSD}</b></span>
              <span>☣ <b style={{ color: '#e63329' }}>{infectedCount}/{activePlayers.length}</b></span>
              <span>SHIELD <b style={{ color: shieldWindowOpen ? '#6b8e23' : '#7d9a72' }}>{shieldWindowOpen ? 'OPEN' : 'CLOSED'}</b></span>
              <span aria-hidden="true" style={{ color: '#6b8e23' }}>{statsOpen ? '▴' : '▾'}</span>
            </span>
          </button>
          {statsOpen && (
            <div className="mt-2 space-y-1.5 rounded-lg border px-3 py-3 md:hidden" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.18)' }}>
              <p className="font-mono text-[11px]" style={{ color: '#8fa882' }}>
                Pot: <span style={{ color: '#f5c518' }}>{potCUSD} USDm</span> — split between the winners.
              </p>
              <p className="font-mono text-[11px]" style={{ color: '#8fa882' }}>
                Infected: <span style={{ color: '#e63329' }}>{infectedCount}</span> of {activePlayers.length} still alive.
                {' '}They win by reaching a majority.
              </p>
              <p className="font-mono text-[11px]" style={{ color: '#8fa882' }}>
                Shield window: <span style={{ color: shieldWindowOpen ? '#6b8e23' : '#7d9a72' }}>{shieldWindowOpen ? 'open' : 'closed'}</span>
                {' '}— Shields can only be activated during Discussion.
              </p>
            </div>
          )}

          <div className="hidden md:grid md:grid-cols-3 gap-3 sm:gap-4">
            {[
              { label: 'POT',          value: `${potCUSD} USDm`, accent: '#f5c518' },
              { label: 'INFECTED',     value: `${infectedCount} / ${activePlayers.length}`, accent: '#e63329' },
              { label: 'SHIELD WINDOW', value: shieldWindowOpen ? 'OPEN' : 'CLOSED', accent: '#6b8e23' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rise-in rounded-lg border p-4 text-center"
                style={{ backgroundColor: '#0a100a', borderColor: `${stat.accent}33` }}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>{stat.label}</p>
                <p className="mt-2 font-heading text-xl sm:text-3xl leading-none" style={{ color: stat.accent }}>{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      </>
      )}

      {/* Main Board */}
      <div className="px-6 py-8">
        <div className="mx-auto w-full max-w-6xl">
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">

            {/* ── LEFT COLUMN ── */}
            <div className="flex flex-col gap-6">

              {/* Host Controls — hoisted to the top of the left column (was below
                  the Area 51 board, below the fold) so the host recruits bots
                  and starts the game without hunting for it. Only during
                  `waiting`, which is mutually exclusive with `starting`. */}
              {room?.status === 'waiting' && isHost && showOnTab('play') && (
                <div className="rise-in rounded-lg border p-5" style={{ borderColor: 'rgba(245,197,24,0.6)', backgroundColor: 'rgba(245,197,24,0.08)', boxShadow: '0 0 16px rgba(245,197,24,0.22)' }}>
                  <p className="font-mono text-xs font-bold uppercase tracking-[0.2em]" style={{ color: '#f5c518' }}>Host Controls</p>
                  <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>{hostPlayerCountLabel}</p>
                  {/* In-room bot recruiting — no lobby round-trip needed */}
                  {(room?.maxPlayers ?? 0) - totalPlayers > 0 && (
                    <div className="toxic-pulse mt-3 rounded">
                      <p className="font-mono text-xs leading-relaxed" style={{ color: '#84cc16' }}>
                        Short on humans? Fill the empty seats with bots — they join in seconds and play for real stakes.
                      </p>
                      <BotControls
                        roomId={BigInt(roomId)}
                        stakeAmount={room?.stakeAmount ?? 0n}
                        freeSeats={(room?.maxPlayers ?? 0) - totalPlayers}
                      />
                    </div>
                  )}
                  {startError && <p className="mt-2 font-mono text-xs" style={{ color: '#e63329' }}>{startError}</p>}
                  <button
                    onClick={handleStartGame}
                    disabled={starting || totalPlayers < (room?.minPlayers ?? 3)}
                    className="mt-3 w-full rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
                    style={{ backgroundColor: '#f5c518', borderColor: '#f5c518', color: '#060b06' }}
                  >
                    {starting ? 'Starting…' : 'Start Game'}
                  </button>
                  {totalPlayers < (room?.minPlayers ?? 3) && (
                    <p className="mt-2 font-mono text-xs" style={{ color: '#7d9a72' }}>Need at least {room?.minPlayers ?? 3} players to start.</p>
                  )}
                </div>
              )}

              {/* Phase card — the round/timer readout, kept at the very top on a
                  phone so "how long have I got" never needs a scroll. */}
              {showOnTab('play') && (
                <div
                  className="rise-in order-1 rounded-lg border p-5"
                  style={{ backgroundColor: phaseCardBackground, borderColor: `${phaseColor}4d` }}
                >
                  {/* Round phase sequence indicator */}
                  {room?.status === 'active' && (
                    <div className="mb-4 flex items-center gap-1 flex-wrap">
                      {((['infection', 'discussion', 'voting', 'reveal'] as RoundPhase[])).map((p, i) => {
                        const phases: RoundPhase[] = ['infection', 'discussion', 'voting', 'reveal']
                        const currentIdx = phases.indexOf(phase)
                        const thisIdx = i
                        const isCurrent = p === phase
                        const isPast = thisIdx < currentIdx
                        const shortLabel: Record<string, string> = { infection: 'INFECT', discussion: 'DISCUSS', voting: 'VOTE', reveal: 'ELIMINATE' }
                        return (
                          <span key={p} className="flex items-center gap-1">
                            <span
                              className="font-mono text-[9px] uppercase tracking-[0.12em]"
                              style={{
                                color: isCurrent ? narrative.phaseColors[phase] : isPast ? '#2a3a24' : '#2a3a24',
                                fontWeight: isCurrent ? 700 : 400,
                                textDecoration: isPast ? 'line-through' : 'none',
                              }}
                            >
                              {shortLabel[p]}
                            </span>
                            {i < 3 && <span className="font-mono text-[9px]" style={{ color: '#2a3a24' }}>→</span>}
                          </span>
                        )
                      })}
                    </div>
                  )}
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>Current Phase</p>
                  <p className="mt-2 font-heading text-3xl leading-none" style={{ color: phaseColor }}>{phaseLabel}</p>
                  <p className="mt-3 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>{phaseCardDescription}</p>
                  {localPlayer?.isEliminated && (
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: '#7d9a72' }}>⊘ You have been eliminated — spectator only.</p>
                  )}
                  {!localPlayer?.isEliminated && localPlayer?.status === 'infected' && (
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: '#e63329' }}>
                      {localPlayer.role === 'patient_zero' ? '☣ You are Patient Zero' : '⚠ You are Infected'}
                    </p>
                  )}
                  {isSpectator && (
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: '#f5c518' }}>👁 Spectator mode — observe only.</p>
                  )}
                </div>
              )}

              {/* Phone-only slot for the Vote Panel — above the cam, which is the
                  whole point. Desktop renders it from the sidebar instead. */}
              <div className="order-3 md:hidden">{votePanel}</div>

              {/* Containment Board (the cam) — sits below the action panels on a
                  phone, see the ordering note on the Action panels block. */}
              {showOnTab('play') && (
                <article className="rise-in order-4 rounded-lg border p-6" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.2)' }}>
                  <div className="flex items-center justify-between gap-4">
                    <h2 className="font-heading text-2xl leading-none" style={{ color: '#d4c9b2' }}>Area 51</h2>
                    <span className="rounded border px-3 py-1 font-mono text-xs uppercase tracking-[0.18em]" style={{ borderColor: 'rgba(107,142,35,0.35)', color: '#6b8e23', backgroundColor: 'rgba(107,142,35,0.1)' }}>
                      {activePlayers.length} alive
                    </span>
                  </div>
                  <OutbreakScene
                    key={roomId ?? 'no-room'}
                    barricade={barricadeView}
                    className="mt-5"
                    totalPlayers={totalPlayers}
                    aliveCount={activePlayers.length}
                    zombieCount={infectedCount}
                    myStatus={localPlayer?.status ?? null}
                    outcome={round > 0 ? (result?.outcome ?? null) : null}
                    socket={socket}
                    localAddress={address}
                    myShieldActive={hasProofThisRound && !localPlayer?.isEliminated}
                    othersShieldCount={othersShieldCount}
                  />

                  {/* The board sits directly under the cam, because the cam is
                      the picture it is a control for: you read which wall is
                      threatened off the scene, then tap a wall here. Splitting
                      the two into separate columns meant looking at one while
                      acting on the other.

                      `disabled` no longer gates on state.next — an armed push.
                      A warning is only live for eight seconds, so the board was
                      inert for most of Discussion: you tapped a wall, nothing
                      moved, and the honest read was that the control was
                      broken. Choosing where to stand is legal all round. */}
                  {phase === 'discussion' && barricade.state && (
                    <div className="mt-6">
                      <BarricadeBoard
                        state={barricade.state}
                        myStation={localPlayer?.isEliminated ? null : barricade.assignedStation}
                        myChoice={barricade.choice}
                        canSabotage={!!localPlayer && !localPlayer.isEliminated && localPlayer.status === 'infected'}
                        disabled={!localPlayer || localPlayer.isEliminated}
                        nameOf={addr => room?.players?.find(
                          p => p.walletAddress.toLowerCase() === addr.toLowerCase(),
                        )?.displayName ?? `${addr.slice(0, 6)}…`}
                        onChoose={barricade.choose}
                      />
                    </div>
                  )}

                  <div className="mt-6 rounded-lg border p-5" style={{ backgroundColor: '#0c1309', borderColor: 'rgba(107,142,35,0.15)' }}>
                    {playersPanelBody}
                  </div>
                  {localPlayer?.isEliminated && (
                    <div className="mt-5 rounded-lg border p-5" style={{ borderColor: 'rgba(74,94,68,0.5)', backgroundColor: 'rgba(74,94,68,0.1)' }}>
                      <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#7d9a72' }}>⊘ YOU HAVE BEEN ELIMINATED</p>
                      <p className="mt-2 font-mono text-xs" style={{ color: '#7d9a72' }}>You can observe but can no longer vote or submit proofs.</p>
                    </div>
                  )}
                  {!localPlayer?.isEliminated && localPlayer?.status === 'infected' && (
                    <div className="mt-5 rounded-lg border p-5" style={{ borderColor: 'rgba(230,51,41,0.6)', backgroundColor: 'rgba(230,51,41,0.12)' }}>
                      <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#e63329' }}>
                        {localPlayer.role === 'patient_zero' ? '☣ YOU ARE PATIENT ZERO' : '⚠ YOU HAVE BEEN INFECTED'}
                      </p>
                      <p className="mt-2 font-mono text-xs" style={{ color: '#ff6b6b' }}>
                        {localPlayer.role === 'patient_zero'
                          ? 'The infected depends on you. Lead the infected faction to parity.'
                          : 'Spread the plague. Avoid suspicion. Infected players cannot submit innocence proofs.'}
                      </p>
                    </div>
                  )}
                  {isSpectator && (
                    <div className="mt-5 rounded-lg border p-5" style={{ borderColor: 'rgba(245,197,24,0.45)', backgroundColor: 'rgba(245,197,24,0.07)' }}>
                      <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#f5c518' }}>👁 SPECTATOR MODE</p>
                      <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>
                        This game was already in progress when you arrived. You can watch but cannot vote, prove innocence, or affect the outcome.
                      </p>
                    </div>
                  )}
                  {localPlayer && (
                    <p className="mt-4 font-mono text-xs" style={{ color: '#7d9a72' }}>
                      You: {localPlayer.displayName}{localPlayer.role !== 'unknown' ? ` · ${localPlayer.role}` : ''}
                    </p>
                  )}
                </article>
              )}

              {/* Action panels (Shield).
                  ── Column ordering ──────────────────────────────────────────
                  The parent is `flex flex-col` at every width, so these blocks
                  are reordered with `order-*` rather than moved in the source —
                  the JSX stays grouped by what it is, not by where it sits on
                  screen. Only the four that matter are numbered:

                    order-1  phase card + timer
                    order-2  action panels (Shield)  ← this block
                    order-3  vote panel (phone only; desktop uses the sidebar)
                    order-4  containment board (the cam + the barricade)
                    order-5  live feed
                    order-6  back to lobby

                  The barricade board moved into order-4, under the cam. It used
                  to sit here at order-2, above Shield, on the argument that it
                  is the only thing in Discussion carrying a deadline. But it is
                  a control for the picture: you read which wall is threatened
                  off the scene and then tap a wall, and having the two in
                  different blocks meant looking at one while acting on the
                  other. Shield is now the first action a player meets, which is
                  the right order anyway — it is the one that costs money.

                  Anything unnumbered keeps order 0 and therefore sorts ABOVE all
                  of these, so a new block in this column needs a number unless
                  the top is genuinely where it belongs. Only the host controls
                  are deliberately unnumbered — they render just while the room
                  is waiting, and the top is right for them.

                  Applies on desktop too. It was gated behind `md:order-none` on
                  the assumption that a wide screen shows everything at once and
                  so could not hide anything — but the cam is tall, and on a
                  laptop it pushed Shield far enough down the column that players
                  did not know the option existed. What you owe the game belongs
                  above the thing you watch, at every width. */}
              {showOnTab('play') && (
                <div className="order-2 flex flex-col gap-6">
                  {phase === 'discussion' && !!localPlayer && !localPlayer.isEliminated && localPlayer.status !== 'infected' && !hasProofThisRound && (
                    <div className="rise-in rounded-lg border p-5" style={{ borderColor: 'rgba(107,142,35,0.35)', backgroundColor: 'rgba(107,142,35,0.08)' }}>
                      <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#6b8e23' }}>Activate Shield</p>
                      <p className="mt-2 font-mono text-xs leading-relaxed" style={{ color: '#8fa882' }}>
                        Proves you are clean, before voting opens.
                        {localPlayer?.freeProofUsed ? ' Your free one is used — this costs the Shield fee.' : ' Your first one is free.'}
                      </p>
                      <PasswordInput
                        placeholder="Your Shield Password…"
                        value={secretPhrase}
                        onChange={setSecretPhrase}
                        className="mt-3"
                      />
                      {proofError && <p className="mt-2 font-mono text-xs" style={{ color: '#e63329' }}>{proofError}</p>}
                      <button
                        onClick={handleSubmitProof}
                        disabled={!canProve || !secretPhrase || proving}
                        className="mt-3 w-full rounded border py-2 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
                        style={{ backgroundColor: '#6b8e23', borderColor: '#6b8e23', color: '#060b06' }}
                      >
                        {proving ? 'Activating Shield…' : 'Activate Shield'}
                      </button>
                    </div>
                  )}

                  {phase === 'discussion' && hasProofThisRound && (
                    <p className="font-mono text-xs" style={{ color: '#84cc16' }}>✓ Shield activated on-chain.</p>
                  )}

                  {result && (
                    <article className="rise-in rounded-lg border p-6" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(132,204,22,0.3)' }}>
                      <h3 className="font-heading text-2xl" style={{ color: '#84cc16' }}>GAME OVER</h3>
                      <p className="mt-2 font-display text-4xl" style={{ color: '#f5c518' }}>{getResultLabel(gameOutcome === 'aborted' ? 'aborted' : result.outcome)}</p>
                      {gameOutcome === 'aborted' ? (
                        <p className="mt-3 font-mono text-sm" style={{ color: '#8fa882' }}>The game never started — every player&apos;s stake was refunded in full.</p>
                      ) : (
                        <>
                          <p className="mt-3 font-mono text-sm" style={{ color: '#8fa882' }}>Pot per winner: {potPerWinnerDisplay} USDm</p>
                          <p className="mt-1 font-mono text-xs" style={{ color: '#7d9a72' }}>
                            Winners: {winnerNames.join(', ') || '—'}
                          </p>
                        </>
                      )}
                      {/* The only exit used to be a link in the right sidebar,
                          which on mobile lives behind the Feed tab — so a
                          player who finished a game on the Game tab had no
                          visible way out. */}
                      <div className="mt-5 flex flex-wrap gap-3">
                        <Link
                          href="/lobby"
                          className="rounded border px-5 py-2.5 font-mono text-sm font-bold uppercase tracking-wider transition-all hover:opacity-90"
                          style={{ backgroundColor: '#6b8e23', borderColor: '#6b8e23', color: '#060b06' }}
                        >
                          Play Again →
                        </Link>
                        <Link
                          href="/leaderboard"
                          className="rounded border px-5 py-2.5 font-mono text-sm uppercase tracking-wider transition-all hover:opacity-90"
                          style={{ borderColor: 'rgba(212,201,178,0.4)', color: '#d4c9b2' }}
                        >
                          Leaderboard
                        </Link>
                      </div>
                    </article>
                  )}
                </div>
              )}

              {/* Live Feed — Feed tab (mobile) / always (desktop) */}
              {/* order-5 keeps the feed at the BOTTOM. Default order is 0, which
                  sorts ABOVE everything numbered, so dropping `md:order-none`
                  silently lifted it to the top of the desktop layout. It is a
                  log — it belongs under the game, not over it. */}
              {showOnTab('feed') && (
                <div className="rise-in order-5 rounded-lg border p-5" style={{ backgroundColor: '#0a100a', borderColor: 'rgba(107,142,35,0.15)' }}>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] mb-3" style={{ color: '#6b8e23' }}>Live Feed</p>
                  <ul
                    className="space-y-2 font-mono text-xs overflow-y-auto"
                    style={{ color: '#8fa882', maxHeight: isMobile ? 'calc(100vh - 260px)' : '24rem', scrollbarWidth: 'thin' }}
                  >
                    {synchronizedFeed.length > 0 ? synchronizedFeed.map((msg, i) => (
                      <li key={`${msg}-${i}`} className="flex gap-2">
                        <span style={{ color: '#7d9a72' }}>→</span> {msg}
                      </li>
                    )) : (
                      <li style={{ color: '#7d9a72' }}>No recent events yet.</li>
                    )}
                  </ul>
                </div>
              )}

            </div>

            {/* ── RIGHT SIDEBAR ── */}
            <aside className="flex flex-col gap-6">

              {/* Vote Panel renders on a phone from the LEFT column (see
                  `votePanel` and the ordering note on the Action panels block).
                  This slot is the desktop one, where the sidebar is fine. */}
              <div className="hidden md:block">{votePanel}</div>

              {/* Chat — Chat tab (mobile) / always (desktop) */}
              {showOnTab('chat') && (
                <div
                  className="rise-in rounded-lg border p-4 flex flex-col"
                  style={{
                    backgroundColor: '#0a100a',
                    borderColor: 'rgba(107,142,35,0.15)',
                    animationDelay: '160ms',
                    ...(isMobile ? { height: 'calc(100svh - 180px)', maxHeight: 'calc(100svh - 180px)' } : {}),
                  }}
                >
                  <p className="font-mono text-xs uppercase tracking-[0.2em] flex-shrink-0" style={{ color: '#6b8e23' }}>Room Chat</p>
                  <div
                    ref={chatScrollRef}
                    onScroll={e => {
                      const el = e.currentTarget
                      // 40px of slack so "close enough to the bottom" still counts
                      // as pinned after smooth-scroll momentum settles.
                      chatPinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
                    }}
                    className="mt-3 overflow-y-auto space-y-2 pr-1 flex-1 min-h-0"
                    style={{
                      // Desktop has no fixed container height; cap so the list
                      // scrolls instead of growing past the viewport.
                      ...(isMobile ? {} : { flex: '0 1 auto', maxHeight: 'min(24rem, calc(100dvh - 300px))' }),
                      scrollbarWidth: 'thin',
                    }}
                  >
                    {chatMessages.length === 0 ? (
                      <p className="font-mono text-[11px]" style={{ color: '#7d9a72' }}>No messages yet…</p>
                    ) : (
                      chatMessages.map((m, i) => (
                        <div key={`${m.timestamp}-${m.sender}-${i}`} className="font-mono text-[11px] leading-snug break-words">
                          <span style={{ color: m.sender.toLowerCase() === address?.toLowerCase() ? '#6b8e23' : '#f5c518' }}>
                            {m.displayName}
                          </span>
                          <span style={{ color: '#7d9a72' }}>: </span>
                          <span style={{ color: '#d4c9b2' }}>{m.message}</span>
                        </div>
                      ))
                    )}
                  </div>
                  {/* Live composer indicator. Names are resolved from the same
                      player list the cards use, so it automatically inherits
                      whatever naming rule is in force — including the No Names
                      modifier's seat numbers. Reserves its own line height so
                      appearing and vanishing never reflows the chat log. */}
                  <div
                    className="mt-1 h-4 flex-shrink-0 overflow-hidden font-mono text-[10px] italic"
                    style={{ color: '#7d9a72' }}
                    aria-live="polite"
                  >
                    {typingAddrs.length > 0 && (
                      <span style={{ animation: 'presence-fade 1.8s ease-in-out infinite' }}>
                        {typingAddrs.length === 1
                          ? `${room?.players?.find(p => p.walletAddress.toLowerCase() === typingAddrs[0])?.displayName ?? 'Someone'} is typing…`
                          : `${typingAddrs.length} players are typing…`}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex gap-2 flex-shrink-0">
                    <input
                      type="text"
                      placeholder={chatBlockedReason ?? 'Say something…'}
                      value={chatInput}
                      maxLength={256}
                      onChange={e => {
                        setChatInput(e.target.value)
                        noteActivity(e.target.value.trim().length > 0)
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') handleSendChat() }}
                      disabled={!canChat}
                      className="flex-1 rounded border bg-transparent px-3 py-1.5 font-mono text-xs focus:outline-none"
                      style={{ borderColor: 'rgba(107,142,35,0.3)', color: '#d4c9b2' }}
                    />
                    <button
                      onClick={handleSendChat}
                      disabled={!chatInput.trim() || !canChat}
                      className="rounded border px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-all hover:opacity-90 disabled:opacity-40"
                      style={{ borderColor: '#6b8e23', color: '#6b8e23' }}
                    >
                      Send
                    </button>
                  </div>
                  {chatBlockedReason && (
                    <p className="mt-2 font-mono text-[11px]" style={{ color: '#f5c518' }}>{chatBlockedReason}</p>
                  )}
                </div>
              )}

              {/* Back to Lobby — Feed tab (mobile) / always (desktop) */}
              {showOnTab('feed') && (
                <Link
                  href="/lobby"
                  className="rise-in order-6 rounded-lg border py-4 text-center font-mono text-sm uppercase tracking-[0.18em] transition-all hover:opacity-90"
                  style={{ borderColor: 'rgba(107,142,35,0.35)', backgroundColor: 'rgba(107,142,35,0.08)', color: '#6b8e23', display: 'block', animationDelay: '300ms' }}
                >
                  ← Back to Lobby
                </Link>
              )}

            </aside>
          </div>
        </div>
      </div>
      </div>

      {/* Mobile floating countdown chip — visible across all tabs */}
      {isMobile && headerCountdownMs > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-xs font-bold tabular-nums shadow-lg"
          style={{
            bottom: 'calc(60px + env(safe-area-inset-bottom, 0px) + 8px)',
            borderColor: 'rgba(107,142,35,0.55)',
            backgroundColor: 'rgba(6,11,6,0.92)',
            color: '#6b8e23',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            textShadow: '0 0 8px rgba(107,142,35,0.5)',
          }}
        >
          <span className="text-[9px] font-normal uppercase tracking-[0.18em]" style={{ color: '#7d9a72' }}>
            {phaseLabel}
          </span>
          <span>{formatCountdown(headerCountdownMs)}</span>
        </div>
      )}

      {/* Mobile Tab Bar */}
      {isMobile && (
        <GameTabNav activeTab={activeTab} onTabChange={handleTabChange} unreadChat={unreadChat} />
      )}
    </main>
  )
}

function getContractClient() {
  const network = (process.env.NEXT_PUBLIC_NETWORK ?? 'testnet') as 'testnet' | 'mainnet'
  const addr    = process.env.NEXT_PUBLIC_CONTRACT_ADDRESS as `0x${string}` | undefined
  if (!addr) return null
  return createContractClient({ contractAddress: addr, network })
}

export default function GamePage() {
  return (
    <Suspense fallback={
      <main className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#060b06' }}>
        <p className="font-mono text-sm" style={{ color: '#7d9a72' }}>Loading game…</p>
      </main>
    }>
      <GamePageInner />
    </Suspense>
  )
}

